import { useEffect, useState } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

/** One document the withdrawal takes with it, as the consequence line names it. */
export interface VoidDependent {
    /** How it is spoken about mid-sentence — `INSTRUMENT_NOUNS`, never the doc type. */
    noun: string;
    /** How far its signing had got: `signed by 2 of 2 parties`. */
    state: string;
}

interface VoidReissueDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    documentTitle: string;
    referenceNo: string;
    /** COMPLETED documents need an explicit reason; SQL refuses a blank one. */
    requiresReason: boolean;
    /**
     * What VOIDING this document destroys alongside it [D-B44] — empty everywhere the question
     * does not arise. Named here rather than computed here: the dependency is a fact about the
     * templates, and `citationCascade` is where that fact is written down and tested.
     */
    dependents?: VoidDependent[];
    /**
     * One sentence about what the REPLACEMENT is made of, where "from the current contract values"
     * is worth spelling out [D-B48].
     *
     * The Vehicle Schedule copies one figure onto its own record: the contract's cash price, which
     * `agreement_create` re-copies on the reissue [D-B59]. It is the figure the partner's returns
     * are measured against and it may have moved since the withdrawn schedule copied it, so the
     * note names it rather than leave the box's own label to cover it.
     */
    reissueNote?: string | null;
    /**
     * Why the REPLACEMENT cannot be generated, or null — `boardSlots.reissueRefusalFor` [D-B59].
     *
     * Shown in the note's place and holding the confirm shut while the reissue box is ticked, so
     * nothing is voided that the reissue would then be refused. Unticking the box is a plain
     * withdrawal, which never asks the question.
     */
    reissueRefusal?: string | null;
    isPending: boolean;
    onConfirm: (reason: string, reissue: boolean) => void;
}

/** `a, b and c` — the same shape the hazard confirms use. */
function list(items: string[]): string {
    if (items.length <= 1) return items[0] ?? '';
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** Every noun above is written for mid-sentence use; this is how one starts one. */
function sentenceCase(text: string): string {
    return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;
}

/**
 * Void, and optionally reissue.
 *
 * Voiding is how a declined signature is recovered and how a document whose figures moved
 * is replaced: it releases the live-document slot, voids the unsigned parties, revokes any
 * signing link and clears the write-back this subsystem made.
 *
 * NOTHING IS BEYOND IT [D-B43]. The completed witness agreement was the last instrument SQL
 * refused, and that refusal is gone: every completed document, of every type, is withdrawable at
 * any contract status. What was always true of the hire purchase agreement is now true of all of
 * them — the figures an instrument quotes can simply be wrong, and a signed copy quoting the wrong
 * ones is the worse fact.
 *
 * SO THE REASON IS THE ENTIRE CEREMONY. It is the only thing standing between this dialog and an
 * executed document, and it is the audit trail the records list shows in the withdrawn document's
 * place. It is also the reason filed against every document the withdrawal carries with it — one
 * admin's sentence, once, for one act.
 *
 * AND THE ACT CAN BE BIGGER THAN THE DOCUMENT IT WAS OPENED ON [D-B44]. Two other executed
 * instruments quote the hire purchase agreement's reference number in frozen text, so withdrawing
 * it takes them with it in the same gesture — and the box below decides only whether replacements
 * are minted for the three. `dependents` is that list; the dialog's job is to make sure nobody
 * presses this button without reading it.
 */
export default function VoidReissueDialog({
    open,
    onOpenChange,
    documentTitle,
    referenceNo,
    requiresReason,
    dependents,
    reissueNote,
    reissueRefusal,
    isPending,
    onConfirm,
}: VoidReissueDialogProps) {
    const [reason, setReason] = useState('');
    const [reissue, setReissue] = useState(true);

    useEffect(() => {
        if (open) {
            setReason('');
            setReissue(true);
        }
    }, [open]);

    const refused = reissue && !!reissueRefusal;
    const ready = (!requiresReason || reason.trim().length > 0) && !refused;

    /**
     * THE DEPENDENTS ARE THE VOID'S CONSEQUENCE, NOT THE REISSUE'S [D-B44, corrected 2026-08-30].
     *
     * This read `reissue ? dependents : []`, on the reasoning that what makes their text false is
     * the NEW reference number this document is about to be given, so a withdrawal minting none
     * left them citing an instrument that still exists. It does not: the instrument they cite is
     * the one being withdrawn, and an admin who unticks the box kills the reference just as dead.
     * Contract 319 proved it in production — a hire purchase agreement voided without reissue,
     * and its witness and insurance documents left quoting a reference no document carries.
     *
     * SO THE LINE AND THE COUNT STAND WHATEVER THE BOX SAYS. Only the verb moves with it.
     */
    const carried = dependents ?? [];

    /** Why their text goes stale: the void kills the reference, the reissue moves it. */
    const staleBecause = reissue
        ? 'the reissue mints a new one'
        : 'withdrawing this document leaves that reference naming nothing';
    /** And what becomes of them — the void half is unconditional, the reissue half is the box. */
    const doneToThem = reissue ? 'voided and reissued' : 'voided';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Void the {documentTitle}?</DialogTitle>
                    <DialogDescription>
                        {referenceNo} — every signature already taken on it stops counting, any signing link
                        is revoked, and the legacy agreement row this document wrote is cleared.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2">
                    <Label htmlFor="void-reason" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Reason {requiresReason ? '*' : '(optional)'}
                    </Label>
                    <Textarea
                        id="void-reason"
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        placeholder="Why is this document being withdrawn?"
                        rows={3}
                    />
                    {requiresReason && (
                        <p className="text-xs text-muted-foreground">
                            This document is complete and every party has signed it. A reason is required and
                            is what the records list shows in its place.
                        </p>
                    )}
                </div>

                <label className="flex cursor-pointer items-start gap-3 text-sm">
                    <Checkbox
                        checked={reissue}
                        onCheckedChange={(value) => setReissue(value === true)}
                        className="mt-0.5"
                    />
                    <span>
                        Generate a fresh document straight afterwards, from the current contract values.
                    </span>
                </label>

                {refused ? (
                    <p className="-mt-1 pl-7 text-xs text-destructive">{reissueRefusal}</p>
                ) : reissue && reissueNote ? (
                    <p className="-mt-1 pl-7 text-xs text-muted-foreground">{reissueNote}</p>
                ) : null}

                {carried.length > 0 && (
                    <p className="text-sm text-destructive">
                        {sentenceCase(list(carried.map((row) => `the ${row.noun} (${row.state})`)))}{' '}
                        {carried.length === 1 ? 'goes' : 'go'} with it:{' '}
                        {carried.length === 1 ? 'it quotes' : 'each quotes'} this document&rsquo;s
                        reference number, and {staleBecause}. They are {doneToThem} in the same
                        act, and every signature already taken on them stops counting.
                        {!reissue && ' Nothing is generated in their place.'}
                    </p>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={() => onConfirm(reason.trim(), reissue)}
                        disabled={!ready || isPending}
                    >
                        {isPending ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <RotateCcw className="mr-2 h-4 w-4" />
                        )}
                        {/* THE COUNT IS THE WHOLE WARNING. A button reading "Void" over a gesture
                            that destroys three executed documents is the surprise this dialog
                            exists to prevent, so the number goes on the button an admin is
                            actually pressing — on both halves, because both halves destroy all
                            three. Only the verb tells them apart. */}
                        {carried.length > 0
                            ? reissue
                                ? `Void and reissue ${carried.length + 1} documents`
                                : `Void ${carried.length + 1} documents`
                            : reissue
                              ? 'Void and reissue'
                              : 'Void'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
